/**
 * Billing / Pricing Page (Story 2.3, Task 3; Story 2.4, Tasks 5-6; Story 2.7)
 *
 * Displays plan comparison with pricing cards, monthly/annual toggle,
 * and upgrade CTAs. Owner-only access guard.
 *
 * For existing subscribers: shows subscription status section with
 * personalized "Switch to Annual" CTA (Story 2.4) and "Manage Subscription"
 * button that opens Stripe Customer Portal (Story 2.7).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@provenleap/ui";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { apiGet, apiPatch, apiPost } from "../../../lib/apiClient";
import {
  CheckIcon,
  LoadingSpinner,
  LockIcon,
  ApplePayIcon,
  GooglePayIcon,
} from "../../../components/icons";
import {
  PLAN_LIMITS,
  PLAN_HIERARCHY,
  PLAN_PRICING,
  PLAN_DISPLAY_NAMES,
  formatFeatureValue,
} from "@provenleap/types";
import type { Plan, PlanLimits, PlanFeature } from "@provenleap/types";
import { PlanLimitsCard } from "../../../components/billing/PlanLimitsCard";
import { OverageCrossoverNudge } from "../../../components/billing/OverageCrossoverNudge";

export const Route = createFileRoute("/_authenticated/settings/billing")({
  component: BillingPage,
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type BillingInterval = "monthly" | "annual";

const PRICING = PLAN_PRICING;

const PLAN_DESCRIPTIONS: Record<Plan, string> = {
  free: "For freelancers",
  starter: "For growing small teams",
  pro: "For established agencies",
  agency: "For large agencies",
};

/** Ordered list of features for comparison (excludes schedulingFrequency). */
interface FeatureRow {
  key: keyof PlanLimits;
  label: string;
  /** Whether to show in collapsed mobile view (top 4 most differentiating). */
  priority: boolean;
}

const FEATURE_ROWS: FeatureRow[] = [
  { key: "clientLimit", label: "Clients", priority: true },
  { key: "teamMembers", label: "Team Members", priority: true },
  { key: "pdfBranding", label: "Branding", priority: true },
  { key: "goalsAlertsTasks", label: "Goals, Alerts & Tasks", priority: true },
  { key: "customDomain", label: "Custom Domain", priority: true },
  { key: "restApiAccess", label: "REST API Access", priority: true },
  { key: "prioritySupport", label: "Priority Support", priority: true },
];

// ---------------------------------------------------------------------------
// Subscription data types (Story 2.4)
// ---------------------------------------------------------------------------

interface SubscriptionData {
  plan: string;
  entitlementPlan?: string;
  accessSuspended?: boolean;
  interval: BillingInterval | null;
  currentPeriodEnd: string;
  status: string;
  subscriptionStatus?: string | null;
  overageBillingEnabled?: boolean;
  clientCount?: number;
}

interface PreviewData {
  quoteId: string;
  credit: number;
  charge: number;
  netAmount: number;
  discount: number;
  tax: number;
  newBillingDate: string;
  currency: string;
}

// Story 2.6: Billing Dashboard types
interface InvoiceData {
  id: string;
  date: number;
  amountPaid: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
  hostedUrl: string | null;
  lines?: Array<{
    id: string;
    description: string | null;
    amount: number;
    currency: string;
    quantity: number | null;
    proration: boolean;
  }>;
  linesHasMore?: boolean;
}

interface PaymentMethodData {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/** Format cents to dollars display string. */
function formatCents(cents: number): string {
  return (Math.abs(cents) / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBillingValue(key: PlanFeature, value: PlanLimits[PlanFeature]): string {
  return formatFeatureValue(key, value, "ProvenLeap branding");
}

function isPlanHigher(target: Plan, current: Plan): boolean {
  return PLAN_HIERARCHY.indexOf(target) > PLAN_HIERARCHY.indexOf(current);
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Map subscription status to human-readable label + styling. */
function getStatusDisplay(status: string): { label: string; className: string } {
  switch (status) {
    case "active":
      return { label: "Active", className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" };
    case "past_due":
      return { label: "Past Due", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" };
    case "canceled":
      return { label: "Canceled", className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" };
    case "incomplete":
      return { label: "Incomplete", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" };
    default:
      return { label: status, className: "bg-muted text-muted-foreground" };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function BillingPage() {
  const { plan: currentPlan } = usePlanLimits();
  const { hasPermission } = usePermission();
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [loadingPlan, setLoadingPlan] = useState<Plan | null>(null);
  const [redirectTarget, setRedirectTarget] = useState<"checkout" | "portal" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Record<Plan, boolean>>({
    free: false,
    starter: false,
    pro: false,
    agency: false,
  });

  // Subscription state (Story 2.4, Task 5)
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(false);

  // Fetch subscription data on mount
  const fetchSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    setSubscriptionError(false);
    try {
      const res = await apiGet("/api/billing/subscription");
      if (!res.ok) {
        setSubscriptionError(true);
        return;
      }
      const { data } = await res.json();
      setSubscriptionData(data);
    } catch {
      setSubscriptionError(true);
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Stripe's Cancel button only redirects; it does not invalidate the open
  // Session. The server-provided intent ID lets us expire the owned Stripe
  // Session first and then release the local client-count/checkout fence.
  const checkoutCancellationStartedRef = useRef(false);
  useEffect(() => {
    const intentId = new URLSearchParams(window.location.search).get(
      "checkout_cancel",
    );
    if (!intentId || checkoutCancellationStartedRef.current) return;
    checkoutCancellationStartedRef.current = true;

    void (async () => {
      try {
        const response = await apiPost("/api/billing/checkout/cancel", {
          intentId,
        });
        if (!response.ok) {
          setError(
            "Checkout cancellation is still being verified. Please reload to retry.",
          );
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.delete("checkout_cancel");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        await fetchSubscription();
      } catch {
        setError(
          "Checkout cancellation is still being verified. Please reload to retry.",
        );
      }
    })();
  }, [fetchSubscription]);

  // Story 2.6: Billing dashboard state
  const [invoices, setInvoices] = useState<InvoiceData[] | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodData | null>(null);
  const [pmLoading, setPmLoading] = useState(true);
  const dashboardFetchedRef = useRef(false);

  // Story 2.7: Portal session state
  const [portalLoading, setPortalLoading] = useState(false);

  // Fetch invoices + payment method once when subscription confirms a paid plan
  const subscriptionPlan = subscriptionData?.plan;
  useEffect(() => {
    if (!subscriptionData || subscriptionPlan === "free") {
      setInvoicesLoading(false);
      setPmLoading(false);
      return;
    }

    // Only fetch once — avoid re-fetching on optimistic updates
    if (dashboardFetchedRef.current) return;
    dashboardFetchedRef.current = true;

    setInvoicesLoading(true);
    setPmLoading(true);

    let cancelled = false;

    (async () => {
      try {
        const res = await apiGet("/api/billing/invoices");
        if (!cancelled && res.ok) {
          const { data } = await res.json();
          setInvoices(data.invoices);
        }
      } catch { /* graceful degradation — shows empty state */ } finally { if (!cancelled) setInvoicesLoading(false); }
    })();

    (async () => {
      try {
        const res = await apiGet("/api/billing/payment-method");
        if (!cancelled && res.ok) {
          const { data } = await res.json();
          setPaymentMethod(data);
        }
      } catch { /* graceful degradation — shows "no payment method" */ } finally { if (!cancelled) setPmLoading(false); }
    })();

    return () => { cancelled = true; };
  }, [subscriptionPlan, subscriptionData]);

  // Personalized annual savings for monthly subscribers
  const annualSavings = useMemo(() => {
    if (!subscriptionData?.plan || subscriptionData.plan === "free") return 0;
    const plan = subscriptionData.plan as Exclude<Plan, "free">;
    if (!PRICING[plan]) return 0;
    return PRICING[plan].monthly * 12 - PRICING[plan].annual;
  }, [subscriptionData?.plan]);

  const isSubscriber = subscriptionData !== null && subscriptionData.plan !== "free";
  const localSubscriptionStatus =
    subscriptionData?.subscriptionStatus === undefined
      ? subscriptionData?.status
      : subscriptionData.subscriptionStatus;
  const displaySubscriptionStatus =
    localSubscriptionStatus ?? subscriptionData?.status ?? "unknown";
  const isActiveMonthly =
    subscriptionData?.status === "active" &&
    localSubscriptionStatus === "active" &&
    subscriptionData?.interval === "monthly";

  // Confirmation dialog state (Story 2.4, Task 6)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<{ amount: string; date: string } | null>(null);

  /** Opens the confirmation dialog and fetches proration preview. */
  async function handlePreviewAndSwitch() {
    setDialogOpen(true);
    setPreviewData(null);
    setPreviewError(false);
    setSwitchError(null);
    setPreviewLoading(true);

    try {
      const res = await apiGet("/api/billing/subscription/preview-switch");
      if (!res.ok) {
        setPreviewError(true);
        return;
      }
      const { data } = await res.json();
      if (!data || typeof data.quoteId !== "string" || data.quoteId.trim() === "") {
        setPreviewError(true);
        return;
      }
      setPreviewData(data as PreviewData);
    } catch {
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  }

  /** Confirms the interval switch via PATCH. */
  async function handleIntervalSwitch() {
    if (!previewData) return;
    setSwitchError(null);
    setSwitchLoading(true);

    try {
      const res = await apiPatch("/api/billing/subscription", {
        interval: "annual",
        quoteId: previewData.quoteId,
      });

      if (!res.ok) {
        const body = await res.json();
        const msg = body?.errors?.[0]?.message ?? body?.detail ?? "Failed to switch to annual billing.";
        setSwitchError(msg);
        setSwitchLoading(false);
        return;
      }

      const { data } = await res.json();

      // Close dialog
      setDialogOpen(false);
      setSwitchLoading(false);

      // Show persistent success banner
      setSuccessBanner({
        amount: `$${formatCents(previewData?.netAmount ?? 0)}`,
        date: formatDate(data.nextBillingDate),
      });

      // Optimistic UI update
      setSubscriptionData((prev) =>
        prev ? { ...prev, interval: "annual", currentPeriodEnd: data.nextBillingDate } : prev
      );

      // Background re-fetch after 1s delay for eventual consistency
      setTimeout(() => {
        fetchSubscription();
      }, 1000);
    } catch {
      setSwitchError("Something went wrong. Please check your subscription status.");
      setSwitchLoading(false);
    }
  }

  // Owner-only access guard
  if (!hasPermission(Actions.MANAGE_BILLING)) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto" data-testid="billing-no-permission">
        <Link to="/settings" className="text-sm text-muted-foreground hover:underline mb-4 inline-block">
          &larr; Back to Settings
        </Link>
        <Card>
          <CardHeader>
            <CardTitle>Billing</CardTitle>
            <CardDescription>
              Only the account owner can manage billing.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  async function handleUpgrade(targetPlan: Exclude<Plan, "free">) {
    setError(null);
    setLoadingPlan(targetPlan);

    // For existing subscribers, use their current billing interval (toggle is hidden).
    // For free users, use the toggle state.
    const checkoutInterval = subscriptionData?.interval ?? interval;

    try {
      const res = await apiPost("/api/billing/checkout", {
        plan: targetPlan,
        interval: checkoutInterval,
      });

      if (!res.ok) {
        const body = await res.json();
        const msg =
          body?.errors?.[0]?.message ?? body?.detail ?? "Failed to start checkout";
        setError(msg);
        setLoadingPlan(null);
        return;
      }

      const { data } = await res.json();
      // Store expected plan for success page polling
      sessionStorage.setItem("expectedPlan", targetPlan);
      sessionStorage.setItem("expectedInterval", checkoutInterval);

      setRedirectTarget("checkout");
      window.location.href = data.url;
    } catch {
      setError("Something went wrong. Please try again.");
      setLoadingPlan(null);
    }
  }

  // Story 2.7: Open Stripe Customer Portal
  async function handleManageSubscription() {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await apiPost("/api/billing/portal-session");
      if (!res.ok) {
        const body = await res.json();
        const msg =
          body?.errors?.[0]?.message ??
          body?.detail ??
          "Failed to open subscription portal.";
        setError(msg);
        return;
      }
      const { data } = await res.json();
      setRedirectTarget("portal");
      window.location.href = data.url;
    } catch {
      setError("Failed to open subscription portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  }

  const plans: Plan[] = ["free", "starter", "pro", "agency"];

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="billing-page">
      <ReportBreadcrumb items={[{ label: "Settings", to: "/settings" }, { label: "Billing" }]} />
      <p className="text-muted-foreground mb-6">
        Choose the plan that fits your agency. Upgrade anytime.
      </p>

      {/* Redirect overlay */}
      {redirectTarget && (
        <div
          className="fixed inset-0 bg-background/80 flex items-center justify-center z-50"
          data-testid="redirect-overlay"
        >
          <div className="text-center">
            <LoadingSpinner className="mx-auto mb-3" />
            <p className="text-lg font-medium">
              {redirectTarget === "portal" ? "Redirecting to subscription portal..." : "Redirecting to secure checkout..."}
            </p>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          className="bg-destructive/10 text-destructive border border-destructive/20 rounded-md p-3 mb-6"
          data-testid="checkout-error"
        >
          {error}
        </div>
      )}

      {/* Persistent success banner (Story 2.4, Task 6) */}
      {successBanner && (
        <div
          className="bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800 rounded-md p-3 mb-6 flex items-center justify-between"
          data-testid="switch-success-banner"
        >
          <span>
            Switched to annual billing. Charged {successBanner.amount} today. Next
            billing date: {successBanner.date}.
          </span>
          <button
            type="button"
            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 ml-4 text-lg leading-none"
            onClick={() => setSuccessBanner(null)}
            aria-label="Dismiss"
            data-testid="dismiss-success-banner"
          >
            &times;
          </button>
        </div>
      )}

      {/* Plan limits card (Story 2.9, AC #7) — shows usage vs limits */}
      {/* TODO Story 3.2 / Story 1.13: Fetch the real team-member count. */}
      {!subscriptionLoading && (
        <div className="mb-8">
          <PlanLimitsCard
            clientCount={subscriptionData?.clientCount ?? 0}
            teamMemberCount={0}
          />
        </div>
      )}

      {!subscriptionLoading &&
        !subscriptionError &&
        isSubscriber &&
        displaySubscriptionStatus === "active" &&
        subscriptionData.interval && (
          <div className="mb-8">
            <OverageCrossoverNudge
              plan={subscriptionData.plan as Plan}
              clientCount={subscriptionData.clientCount ?? 0}
              interval={subscriptionData.interval}
              overageEnabled={subscriptionData.overageBillingEnabled ?? false}
              onUpgrade={() => {
                void handleManageSubscription();
              }}
            />
          </div>
        )}

      {/* Billing dashboard section (Story 2.6) — shown for subscribed users */}
      {!subscriptionLoading && !subscriptionError && isSubscriber && (
        <div className="mb-8 space-y-4" data-testid="billing-dashboard">
          {subscriptionData.accessSuspended && (
            <div
              className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100"
              data-testid="billing-access-suspended"
            >
              Paid-plan access is suspended after the payment grace period.
              Update your payment method below to restore your plan and clients.
            </div>
          )}
          {/* Overview cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Plan overview card */}
            <Card className="p-4" data-testid="plan-overview-card">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Current Plan</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">{PLAN_DISPLAY_NAMES[subscriptionData.plan as Plan] ?? subscriptionData.plan}</span>
                  {(() => {
                    const { label, className } = getStatusDisplay(displaySubscriptionStatus);
                    return (
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${className}`}>
                        {label}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-sm text-muted-foreground">
                  {subscriptionData.interval === "annual" ? "Annual" : "Monthly"} billing
                </p>
                <p className="text-sm text-muted-foreground">
                  Next billing: {subscriptionData.currentPeriodEnd ? formatDate(subscriptionData.currentPeriodEnd) : "—"}
                </p>
                {subscriptionData.plan !== "free" && PRICING[subscriptionData.plan as Exclude<Plan, "free">] && (
                  <p className="text-sm text-muted-foreground">
                    ${subscriptionData.interval === "annual"
                      ? PRICING[subscriptionData.plan as Exclude<Plan, "free">].effectiveMonthly
                      : PRICING[subscriptionData.plan as Exclude<Plan, "free">].monthly
                    }/month
                    {subscriptionData.interval === "annual" && " (billed annually)"}
                  </p>
                )}
                {/* Story 2.7: Manage Subscription via Stripe Portal */}
                <Button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  data-testid="manage-subscription"
                  className="mt-3"
                >
                  {portalLoading ? <LoadingSpinner className="mr-2 h-4 w-4" /> : null}
                  Manage Subscription
                </Button>
              </div>
            </Card>

            {/* Payment method card */}
            <Card className="p-4" data-testid="payment-method-card">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Payment Method</h3>
              {pmLoading ? (
                <div className="space-y-2" data-testid="pm-loading">
                  <div className="h-5 bg-neutral-200 rounded w-32 animate-pulse" />
                  <div className="h-4 bg-neutral-200 rounded w-24 animate-pulse" />
                </div>
              ) : paymentMethod ? (
                <div className="space-y-1">
                  <p className="text-sm font-medium capitalize">
                    {paymentMethod.brand} &bull;&bull;&bull;&bull; {paymentMethod.last4}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Expires {String(paymentMethod.expMonth).padStart(2, "0")}/{paymentMethod.expYear}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No payment method on file</p>
              )}
            </Card>
          </div>

          {/* Invoice list */}
          <Card className="p-4" data-testid="invoice-list">
            <h3 className="text-sm font-medium text-muted-foreground mb-3" id="invoices-heading">Recent Invoices</h3>
            {invoicesLoading ? (
              <div className="space-y-2" data-testid="invoices-loading">
                <div className="h-4 bg-neutral-200 rounded w-full animate-pulse" />
                <div className="h-4 bg-neutral-200 rounded w-full animate-pulse" />
                <div className="h-4 bg-neutral-200 rounded w-3/4 animate-pulse" />
              </div>
            ) : invoices && invoices.length > 0 ? (
              <table className="w-full text-sm" aria-labelledby="invoices-heading">
                <thead>
                  <tr className="border-b">
                    <th scope="col" className="text-left py-2 font-medium text-muted-foreground">Date</th>
                    <th scope="col" className="text-left py-2 font-medium text-muted-foreground">Amount</th>
                    <th scope="col" className="text-left py-2 font-medium text-muted-foreground">Status</th>
                    <th scope="col" className="text-right py-2 font-medium text-muted-foreground">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const invoiceDate = new Date(inv.date * 1000).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                    const { className: statusClass } = getStatusDisplay(inv.status === "paid" ? "active" : inv.status);
                    return (
                      <tr key={inv.id} className="border-b last:border-0" data-testid={`invoice-row-${inv.id}`}>
                        <td className="py-2">{invoiceDate}</td>
                        <td className="py-2">
                          <span className="font-medium">${formatCents(inv.amountPaid)}</span>
                          {inv.lines && inv.lines.length > 0 && !inv.linesHasMore && (
                            <ul
                              className="mt-1 space-y-0.5 text-xs text-muted-foreground"
                              data-testid={`invoice-lines-${inv.id}`}
                            >
                              {inv.lines.map((line) => (
                                <li key={line.id} className="flex max-w-md justify-between gap-3">
                                  <span>{line.description ?? "Stripe invoice item"}</span>
                                  <span className="shrink-0">
                                    {line.amount < 0 ? "-" : ""}${formatCents(line.amount)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="py-2">
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${statusClass} capitalize`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="py-2 text-right">
                          {inv.pdfUrl ? (
                            <a
                              href={inv.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline text-xs"
                              data-testid={`invoice-download-${inv.id}`}
                              aria-label={`Download PDF invoice from ${invoiceDate}`}
                            >
                              PDF
                            </a>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="invoices-empty">No invoices yet</p>
            )}
          </Card>
        </div>
      )}

      {/* Subscription status section (Story 2.4, Task 5) — shown for subscribers */}
      {subscriptionLoading && (
        <div className="mb-8 rounded-lg border p-6 animate-pulse" data-testid="subscription-loading">
          <div className="h-5 bg-muted rounded w-40 mb-4" />
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-60" />
            <div className="h-4 bg-muted rounded w-48" />
            <div className="h-4 bg-muted rounded w-56" />
          </div>
        </div>
      )}

      {subscriptionError && !subscriptionLoading && (
        <div
          className="mb-8 rounded-lg border border-destructive/20 bg-destructive/5 p-6"
          data-testid="subscription-error"
        >
          <p className="text-sm text-destructive mb-2">
            Unable to load subscription details. Your current plan: <strong>{PLAN_DISPLAY_NAMES[currentPlan]}</strong>.
          </p>
          <Button
            onClick={fetchSubscription}
            className="text-sm"
            data-testid="subscription-retry"
          >
            Retry
          </Button>
        </div>
      )}

      {!subscriptionLoading && !subscriptionError && isSubscriber && (
        <div className="mb-8" data-testid="subscription-status">
          {/* Hidden data elements for test compatibility */}
          <span className="sr-only" data-testid="sub-plan">{subscriptionData.plan}</span>
          <span className="sr-only" data-testid="sub-interval">
            {subscriptionData.interval === "annual" ? "Annual" : "Monthly"}
          </span>
          <span className="sr-only" data-testid="sub-next-date">
            {formatDate(subscriptionData.currentPeriodEnd)}
          </span>
          <span className="sr-only" data-testid="sub-status">
            {getStatusDisplay(displaySubscriptionStatus).label}
          </span>

          {/* Switch to Annual CTA — only for active monthly subscribers */}
          {isActiveMonthly && annualSavings > 0 && (
            <div className="rounded-lg border p-4" data-testid="switch-annual-cta">
              <Button
                className="w-full sm:w-auto"
                onClick={handlePreviewAndSwitch}
                disabled={previewLoading}
                data-testid="switch-annual-btn"
              >
                {previewLoading ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  `Switch to Annual — Save $${annualSavings}/year`
                )}
              </Button>
            </div>
          )}

          {/* Non-active subscription warning */}
          {displaySubscriptionStatus !== "active" && (
            <div
              className="text-sm text-yellow-800 dark:text-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 rounded-md p-3"
              data-testid="subscription-warning"
            >
              {displaySubscriptionStatus === "canceled"
                ? "Your subscription is scheduled to end at the current period boundary."
                : `Your subscription is ${displaySubscriptionStatus}. Please update your payment method.`}
            </div>
          )}
        </div>
      )}

      {/* Monthly / Annual toggle — only for free users (no subscription) */}
      {!isSubscriber && !subscriptionLoading && (
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="interval-toggle">
          <span
            className={`text-sm font-medium cursor-pointer ${interval === "monthly" ? "text-foreground" : "text-muted-foreground"}`}
            onClick={() => setInterval("monthly")}
            data-testid="toggle-monthly"
          >
            Monthly
          </span>
          <label
            style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: "pointer" }}
            aria-label="Toggle billing interval"
          >
            <input
              type="checkbox"
              checked={interval === "annual"}
              onChange={(e) => setInterval(e.target.checked ? "annual" : "monthly")}
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 9999,
                backgroundColor: interval === "annual" ? "var(--color-primary)" : "#d1d5db",
                transition: "background-color 0.2s",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 2,
                left: interval === "annual" ? 22 : 2,
                width: 20,
                height: 20,
                borderRadius: 9999,
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </label>
          <span
            className={`text-sm font-medium cursor-pointer ${interval === "annual" ? "text-foreground" : "text-muted-foreground"}`}
            onClick={() => setInterval("annual")}
            data-testid="toggle-annual"
          >
            Annual <span className="text-green-600 dark:text-green-400 font-semibold">2 months free</span>
          </span>
        </div>
      )}

      {/* Pricing cards grid */}
      <div
        className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-10"
        data-testid="pricing-grid"
      >
        {plans.map((plan) => {
          const isCurrent = plan === currentPlan;
          const canUpgrade = isPlanHigher(plan, currentPlan);
          const limits = PLAN_LIMITS[plan];
          const isPaid = plan !== "free";
          const pricing = isPaid ? PRICING[plan as Exclude<Plan, "free">] : null;

          return (
            <Card
              key={plan}
              className={`relative flex flex-col ${
                isCurrent ? "border-primary border-2 shadow-lg" : ""
              } ${plan === "pro" ? "md:order-none order-first" : ""}`}
              data-testid={`plan-card-${plan}`}
            >
              {/* Current Plan badge — floating pill above card */}
              {isCurrent && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full"
                  data-testid={`current-plan-badge-${plan}`}
                >
                  Current Plan
                </div>
              )}

              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">{PLAN_DISPLAY_NAMES[plan]}</CardTitle>
                </div>
                <CardDescription>{PLAN_DESCRIPTIONS[plan]}</CardDescription>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                {/* Price */}
                <div className="mb-4">
                  {isPaid && pricing ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold" data-testid={`price-${plan}`}>
                          ${interval === "monthly" ? pricing.monthly : pricing.effectiveMonthly}
                        </span>
                        <span className="text-muted-foreground text-sm">/mo</span>
                      </div>
                      {interval === "annual" && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`annual-total-${plan}`}>
                          Billed as ${pricing.annual}/year
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold" data-testid={`price-${plan}`}>$0</span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                  )}
                </div>

                {/* Inline feature list (top 4 on mobile, all on desktop) */}
                <ul className="space-y-2 mb-6 text-sm flex-1">
                  {FEATURE_ROWS.map((row) => {
                    const value = limits[row.key];
                    if (value === false) return null;
                    const isMobileHidden = !row.priority;
                    return (
                      <li
                        key={row.key}
                        className={`flex items-start gap-2 ${
                          isMobileHidden ? "hidden md:flex" : ""
                        }`}
                      >
                        <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                        <span>
                          <span className="text-muted-foreground">{row.label}:</span>{" "}
                          {formatBillingValue(row.key, value)}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* Mobile: show all features toggle (per-card) */}
                <button
                  type="button"
                  className="text-xs text-primary underline mb-4 md:hidden"
                  onClick={() => setExpandedCards((prev) => ({ ...prev, [plan]: !prev[plan] }))}
                  data-testid={`expand-features-${plan}`}
                >
                  {expandedCards[plan] ? "Show less" : "Compare all features"}
                </button>
                {expandedCards[plan] && (
                  <ul className="space-y-2 mb-4 text-sm md:hidden">
                    {FEATURE_ROWS.filter((r) => !r.priority && limits[r.key] !== false).map((row) => (
                      <li key={row.key} className="flex items-start gap-2">
                        <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                        <span>
                          <span className="text-muted-foreground">{row.label}:</span>{" "}
                          {formatBillingValue(row.key, limits[row.key])}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Trust signals + CTA */}
                {isPaid && (
                  <div className="mt-auto">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3" data-testid="trust-signal">
                      <LockIcon className="h-3.5 w-3.5" />
                      <span>Secure checkout powered by Stripe. Apple Pay, Google Pay, and cards accepted.</span>
                      <ApplePayIcon className="h-4 w-4 shrink-0" />
                      <GooglePayIcon className="h-4 w-4 shrink-0" />
                    </div>
                    {canUpgrade ? (
                      <>
                        <Button
                          className="w-full"
                          disabled={loadingPlan !== null || (isSubscriber && portalLoading)}
                          onClick={() => {
                            if (isSubscriber) {
                              void handleManageSubscription();
                            } else {
                              void handleUpgrade(plan as Exclude<Plan, "free">);
                            }
                          }}
                          data-testid={`upgrade-btn-${plan}`}
                        >
                          {(isSubscriber && portalLoading) || loadingPlan === plan ? (
                            <LoadingSpinner className="h-4 w-4" />
                          ) : isSubscriber ? (
                            `Choose ${PLAN_DISPLAY_NAMES[plan]} in Stripe`
                          ) : (
                            `Upgrade to ${PLAN_DISPLAY_NAMES[plan]}`
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          Cancel anytime
                        </p>
                      </>
                    ) : (
                      isCurrent ? null : (
                        <div data-testid={`no-action-${plan}`} />
                      )
                    )}
                  </div>
                )}

                {/* Free plan has no CTA */}
                {!isPaid && !isCurrent && (
                  <div data-testid={`no-action-${plan}`} />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Full feature comparison table (desktop) */}
      <div className="hidden md:block" data-testid="feature-comparison">
        <h2 className="text-lg font-semibold mb-4">Feature Comparison</h2>
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left p-4 font-medium text-muted-foreground">Feature</th>
                  {plans.map((plan) => (
                    <th key={plan} className="text-center p-4 font-semibold">
                      {PLAN_DISPLAY_NAMES[plan]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((row, i) => (
                  <tr key={row.key} className={i < FEATURE_ROWS.length - 1 ? "border-b border-border/50" : ""}>
                    <td className="p-4 font-medium">{row.label}</td>
                    {plans.map((plan) => {
                      const value = PLAN_LIMITS[plan][row.key];
                      return (
                        <td key={plan} className="p-4 text-center">
                          {value === true ? (
                            <CheckIcon className="h-5 w-5 text-green-500 inline-block" />
                          ) : value === false ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            formatBillingValue(row.key, value)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Interval switch confirmation dialog (Story 2.4, Task 6) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="switch-dialog">
          <DialogHeader>
            <DialogTitle>Switch to Annual Billing</DialogTitle>
            <DialogDescription>
              Review the proration details below before confirming.
            </DialogDescription>
          </DialogHeader>

          {previewLoading && (
            <div className="flex items-center justify-center py-6" data-testid="preview-loading">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          )}

          {!previewLoading && previewData && (
            <div className="space-y-3 text-sm" data-testid="preview-amounts">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Credit for unused monthly time</span>
                <span className="font-medium text-green-600">-${formatCents(previewData.credit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Annual plan charge</span>
                <span className="font-medium">${formatCents(previewData.charge)}</span>
              </div>
              {previewData.discount > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Stripe discounts included in net</span>
                  <span>-${formatCents(previewData.discount)}</span>
                </div>
              )}
              {previewData.tax > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Stripe tax included in net</span>
                  <span>${formatCents(previewData.tax)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Net charge today</span>
                <span>${formatCents(previewData.netAmount)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>New billing date</span>
                <span>{formatDate(previewData.newBillingDate)}</span>
              </div>
            </div>
          )}

          {!previewLoading && previewError && (
            <p className="text-sm text-destructive py-2" data-testid="preview-fallback">
              We couldn't calculate an exact Stripe quote. Close this dialog and try
              again before switching.
            </p>
          )}

          {switchError && (
            <div
              className="bg-destructive/10 text-destructive border border-destructive/20 rounded-md p-3 text-sm"
              data-testid="switch-error"
            >
              {switchError}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={switchLoading}
              data-testid="switch-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleIntervalSwitch}
              disabled={switchLoading || previewLoading || previewError || !previewData}
              data-testid="switch-confirm-btn"
            >
              {switchLoading ? (
                <LoadingSpinner className="h-4 w-4" />
              ) : (
                "Confirm Switch"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
